import { TextField, Menu, Box, Typography, InputAdornment, MenuItem, CircularProgress } from "@mui/material";
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import useDebounce from "../hooks/useDebounce";
import { useTranslation } from 'react-i18next';
import Iconify from "../components/Iconify";

// Memoized component for the dropdown input shape
const DropDownInputShape = React.memo(({handleRemove, value, className, placeholder, label, required, error, helperText, handleOpen, handleBlur, dropdownOpen }) => (
<TextField
    placeholder={placeholder}
    label={label}
    fullWidth
    value={value}
    required={required}
    InputLabelProps={{ shrink: false }}
    InputProps={{
        readOnly: true,
        endAdornment: (
            <Box display="flex" alignItems="center">
                {value && (
                    <Iconify
                        icon="ic:baseline-clear"
                        onClick={(event) => {
                            event.stopPropagation(); // Prevent triggering the dropdown
                            handleRemove(); // Function to reset the value
                        }}
                        style={{
                            cursor: "pointer",
                            marginRight: 8, // Add spacing between icons
                            color: "#999",
                        }}
                    />
                )}
                <Iconify
                    icon={dropdownOpen ? "iconamoon:arrow-down-2-light" : "iconamoon:arrow-right-2-light"}
                    onClick={handleOpen}
                    style={{ cursor: "pointer" }}
                />
            </Box>
        ),
    }}
    onClick={handleOpen}
    onBlur={handleBlur}
    helperText={helperText}
    error={error}
    className={className}
/>
));

// Memoized component for the search input
const SearchInput = React.memo(({ inputValue, handleInputChange, t }) => (
    <TextField
        placeholder='Chercher'
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <Iconify icon='ic:round-search' />
                </InputAdornment>
            ),
            endAdornment: (
                inputValue && (
                    <InputAdornment position="end">
                        <Iconify
                            icon='ic:round-close'
                            onClick={() => handleInputChange('')}
                            sx={{ cursor: 'pointer', fontSize: '20px' }}
                        />
                    </InputAdornment>
                )
            ),
        }}
    />
));

// Main component for the custom single select dropdown
export const CustomSingleSelect = ({
    label,
    value = null,
    onChange,
    fetchFunction,
    required,
    backendSearchable,
    valueField = 'value',
    labelField = 'label',
    error,
    helperText,
    handleBlur = () => { },
    className,
    placeholder,
    creatable = false,
    createFunction,
    disabled = false,
    option = null, // Added option prop
    fetchOnMount = false, // New prop to fetch options on mount
}) => {
    const { t } = useTranslation();    

    const [options, setOptions] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0); // Changed from page to offset
    const [inputValue, setInputValue] = useState('');
    const [creating, setCreating] = useState(false);
    const debouncedInputValue = useDebounce(inputValue, 300);
    const limit = 10;

    const dropdownRef = useRef(null);
    const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
    const dropdownOpen = Boolean(dropdownAnchorEl);
    const loadMoreRef = useRef(null);

    // Function to load options from the backend
    const loadOptions = useCallback(async (currentOffset, searchTerm) => {
        if (option?.length > 0) return; // Skip fetching if options are provided via props
        try {
            setLoading(true);
            const newOptions = await fetchFunction(currentOffset, limit, searchTerm);
            const safeNewOptions = Array.isArray(newOptions) ? newOptions : [];

            setOptions(prevOptions => {
                const safePrevOptions = Array.isArray(prevOptions) ? prevOptions : [];
                // Reset options if offset is 0 (first load or search)
                return currentOffset === 0 ? safeNewOptions : [...safePrevOptions, ...safeNewOptions];
            });

            setHasMore(safeNewOptions.length === limit);
        } catch (error) {
            console.error('Error fetching options:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [fetchFunction, option]);

    // Handle opening the dropdown menu
    const handleDropdownMenuClick = useCallback((event) => {
        setDropdownAnchorEl(event.currentTarget);
        setOffset(0); // Reset offset when opening dropdown
        loadOptions(0, "");
    }, [loadOptions]);

    // Handle closing the dropdown menu
    const handleDropdownMenuClose = useCallback(() => {
        setDropdownAnchorEl(null);
        setOffset(0); // Reset offset when closing
        // Do not clear options if `option` contains data
        if (!option?.length) {
            setOptions([]);
        }
    }, [option]);

    // Fetch options on mount if fetchOnMount is true
    useEffect(() => {
        if (fetchOnMount) {
            loadOptions(0, "");
        }
    }, [fetchOnMount, loadOptions]);

    useEffect(() => {
        setOptions(option?.length > 0 ? option : []);
    }, [option]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                const nextOffset = offset + limit; // Increment by limit instead of page
                loadOptions(nextOffset, inputValue);
                setOffset(nextOffset);
            }
        }, {
            root: dropdownRef.current,
            rootMargin: '0px',
            threshold: 1.0
        });

        const loadMoreElement = loadMoreRef.current;
        if (loadMoreElement) {
            observer.observe(loadMoreElement);
        }

        return () => {
            if (loadMoreElement) {
                observer.unobserve(loadMoreElement);
            }
        };
    }, [hasMore, loading, offset, loadOptions, inputValue]);

    const handleSearchChange = useCallback((newInputValue) => {
        setInputValue(newInputValue);
        if (backendSearchable) {
            setOptions([]);
            setOffset(0); // Reset offset when searching
            loadOptions(0, newInputValue);
        }
    }, [backendSearchable, loadOptions]);

    const handleRemove = useCallback(() => {
        onChange(null);
    }, [onChange]);

    const handleSelectOption = useCallback((newValue) => {
        if (!newValue) return;
        onChange(newValue);
        setInputValue("");
        setDropdownAnchorEl(null);
    }, [onChange]);

    const handleCreateNewOption = useCallback(async () => {
        if (!creatable || !createFunction || !inputValue.trim()) return;

        try {
            setCreating(true);
            const newOption = { [labelField]: inputValue };
            const createdOption = await createFunction(newOption);

            if (createdOption) {
                setOptions(prev => [createdOption, ...prev]);
                handleSelectOption(createdOption);
            }
        } catch (error) {
            console.error('Error creating new option:', error);
        } finally {
            setCreating(false);
            setInputValue("");
            setDropdownAnchorEl(null)
        }
    }, [creatable, createFunction, inputValue, labelField, handleSelectOption]);

    const filteredOptions = useMemo(() => {
        if (backendSearchable) {
            return options;
        }
        const search = debouncedInputValue.toLowerCase();
        return options?.filter(option => {
            const labelText = String(option[labelField]).toLowerCase();
            return labelText.includes(search);
        });
    }, [options, debouncedInputValue, backendSearchable, labelField]);

    const showCreateOption = useMemo(() => {
        if (!creatable || !inputValue.trim()) return false;

        const searchTerm = inputValue.toLowerCase();
        return !filteredOptions.some(option =>
            String(option[labelField]).toLowerCase() === searchTerm
        );
    }, [creatable, inputValue, filteredOptions, labelField]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', pointerEvents: disabled ? 'none' : 'auto', opacity: disabled ? 0.5 : 1 }}>
            <DropDownInputShape
                className={className}
                dropdownOpen={dropdownOpen}
                label={label}
                required={required}
                error={error}
                value={ value ? value[labelField] : ''}
                helperText={helperText}
                handleBlur={handleBlur}
                placeholder={placeholder}
                handleRemove={handleRemove}
                handleOpen={handleDropdownMenuClick}
            />

            <Menu
                ref={dropdownRef}
                anchorEl={dropdownAnchorEl}
                open={dropdownOpen}
                onClose={handleDropdownMenuClose}
                PaperProps={{
                    style: {
                        width: dropdownAnchorEl ? dropdownAnchorEl.getBoundingClientRect().width : 'auto',
                        marginTop: '5px',
                        padding: '0px 5px'
                    },
                }}>
                <SearchInput inputValue={inputValue} handleInputChange={handleSearchChange} t={t} />
                <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                    {filteredOptions?.length > 0 || showCreateOption ? (
                        <>
                            {showCreateOption && (
                                <MenuItem
                                    onClick={handleCreateNewOption}
                                    disabled={creating}
                                    sx={{ fontStyle: 'italic' }}
                                >
                                    {creating ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        `Créer "${inputValue}"`
                                    )}
                                </MenuItem>
                            )}
                            {filteredOptions?.map((option, index) => (
                                <MenuItem
                                    key={index}
                                    onClick={() => handleSelectOption(option)}
                                >
                                    {option[labelField]}
                                </MenuItem>
                            ))}
                        </>
                    ) : (
                        !loading && <MenuItem disabled>Aucun résultat trouvé</MenuItem>
                    )}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                    <Typography ref={loadMoreRef} />
                </Box>
            </Menu>

        </Box>
    );
};